import React from 'react';
import { Callout, DocHeading, DocTable, Shot, Step } from './components';

function PageQuests() {
  return (
    <div>
      <h1 className="text-[2.5rem] leading-[1.1] font-semibold text-white tracking-[-0.02em] mb-3">Quests</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        The <strong className="text-white">QUESTS</strong> tab brings two pages of the game&rsquo;s
        Journal into the menu, with a button that rerolls for you.{' '}
        <strong className="text-white">Daily challenges</strong> rerolls one NPC&rsquo;s daily
        challenge until it offers exactly the reward you want;{' '}
        <strong className="text-white">Fortune Hunters</strong> refreshes the wanted list until the
        person you want is in the slot you want.
      </p>

      <Callout type="warning">
        <strong>This spends Andermant.</strong> Every reroll or refresh costs the game&rsquo;s fee
        (on Daily challenges a free reroll is used first, if you have one). There is no limit: it
        keeps going until it finds what you asked for, until you press <strong>STOP</strong>, or
        until it has to stop &mdash; the game refuses because you are out of Andermant, or no answer
        comes back. The fee and your balance are shown on the card &mdash; look at them before you
        start.
      </Callout>
      <Callout type="info">
        The bot only rerolls. Accepting the challenge or the wanted quest is still up to you, in the
        game. Your choices on these tabs are not saved: pick them again after you restart the bot.
      </Callout>

      <DocHeading>Daily Challenges</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        <strong className="text-white">QUESTS &rsaquo; Daily challenges</strong> has one card,{' '}
        <strong className="text-white">REROLL</strong>, with a row for each of the four NPCs, in the
        same order as the game&rsquo;s Journal. Each row is headed by the NPC&rsquo;s name and tier
        as the game shows them &mdash; for example <em>Father Caelius - Bronze</em>.
      </p>
      <DocTable
        headers={['Control', 'What it does']}
        rows={[
          [<strong className="text-white">Reward</strong>, '“Skip” leaves this NPC alone. Otherwise pick a reward this NPC’s challenges can give at your level: Gold, Badges of Honor, or an item.'],
          [<strong className="text-white">Amount</strong>, 'The exact amount you want (Gold is shown as gold, silver and copper). If it says “not available at your level”, no challenge at your level gives that reward.'],
          [<strong className="text-white">Reroll fee</strong>, 'What one reroll costs in Andermant. A free reroll is used first.'],
          [<strong className="text-white">Andermant</strong>, 'Your current balance.'],
          [<strong className="text-white">REROLL &hellip;</strong>, 'The start button; it carries the name of the NPC you chose. It reads “CHOOSE A REWARD ABOVE” and stays greyed out until a row has a reward. While running it becomes STOP.'],
        ]}
      />
      <Callout type="info">
        <strong>One NPC at a time.</strong> Picking a reward in one row sets the other three to Skip.
        Only that NPC&rsquo;s offered challenge is rerolled &mdash; the others are never touched.
      </Callout>
      <Callout type="info">
        <strong>Exact, not &ldquo;at least&rdquo;.</strong> A bigger amount usually comes with a harder
        challenge, so the bot stops only on the exact number you picked. If you asked for 14, an offer
        of 18 does not count.
      </Callout>

      <Step n={1} title="Pick the reward">
        In the row of the NPC you want, open <strong className="text-white">Reward</strong> and choose
        what you are after.
      </Step>
      <Step n={2} title="Pick the exact amount">
        Open <strong className="text-white">Amount</strong> and choose the number you want.
      </Step>
      <Step n={3} title="Check the cost">
        Compare the <strong className="text-white">Reroll fee</strong> with your{' '}
        <strong className="text-white">Andermant</strong>. A rare reward can take many rerolls.
      </Step>
      <Step n={4} title="Press REROLL">
        The button shows the NPC&rsquo;s name. It is greyed out if that NPC has no challenge on offer
        right now (already accepted or completed). The bot sends one reroll at a time &mdash; the
        next one only goes out after the new challenge has arrived.
      </Step>
      <Step n={5} title="Watch it, or stop it">
        The <strong className="text-white">Status</strong> line under the button tells you where it
        is. Press <strong className="text-white">STOP</strong> at any time. The rows are locked while
        it runs.
      </Step>

      <Shot
        name="quests-01-daily-challenges.png"
        tab="QUESTS › Daily challenges"
        caption="Tarus Dragonslayer set to a 240-gold reward — the button rerolls only him; found after 7 rerolls, 70 Andermant spent."
        shoot="menushot (stage quests): a QUESTS › Daily challenges lap — REROLL kártya négy NPC-vel, Tarus Dragonslayer Reward Gold, Amount 240g, a többi Skip; Reroll fee, Andermant 2450, REROLL Tarus Dragonslayer gomb, Status found (7 rerolls), Andermant spent 70."
      />

      <DocHeading>The Status Line</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        Both pages use the same status line. On Daily challenges it counts{' '}
        <em>rerolls</em>, on Fortune Hunters <em>refreshes</em>.
      </p>
      <DocTable
        headers={['Status', 'What it means']}
        rows={[
          [<strong className="text-white">idle</strong>, 'Nothing started yet.'],
          [<strong className="text-white">running &nbsp;12 rerolls</strong>, 'Working. The number is how many requests have gone out.'],
          [<strong className="text-white">found &nbsp;(12 rerolls)</strong>, 'Green: what you asked for has arrived.'],
          [<strong className="text-white">already there - nothing was spent</strong>, 'It was already on offer when you pressed the button.'],
          [<strong className="text-white">stopped: stopped</strong>, 'You pressed STOP.'],
          [<strong className="text-white">stopped: the game refused - not enough currency</strong>, 'Not enough Andermant for the next one.'],
          [<strong className="text-white">stopped: no answer from the server</strong>, 'A request went out, but no new result came back. It never resends on its own, so it never pays twice — just start again.'],
          [<strong className="text-white">stopped: nothing to reroll</strong>, 'Daily challenges: the NPC has no challenge on offer any more. Fortune Hunters: the slot you picked (or, with Any slot, every slot) has been accepted.'],
          [<strong className="text-white">stopped: could not send / the request did not run / the state could not be read</strong>, 'The game was not ready, e.g. during a loading screen. Start again once your character is in the world.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        Two more rows can appear under it: <strong className="text-white">Last request</strong> shows
        the game&rsquo;s reason when the last request failed (for example &ldquo;not enough
        Andermant&rdquo;), and <strong className="text-white">Andermant spent</strong> shows how much
        Andermant has gone since you pressed the button.
      </p>

      <DocHeading>Fortune Hunters</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        <strong className="text-white">QUESTS &rsaquo; Fortune Hunters</strong> has two cards side by
        side. On the left, <strong className="text-white">FORTUNE HUNTERS</strong> shows the game&rsquo;s
        wanted list: <strong className="text-white">Slot 1</strong> to{' '}
        <strong className="text-white">Slot 4</strong>, each with the wanted person&rsquo;s name in
        their rarity colour (Magic blue, Rare purple, Epic orange, Unique yellow) and the rarity under
        it, followed by &ldquo;accepted&rdquo; for a slot you have already taken. A name turns{' '}
        <strong className="text-white">green</strong> when it is the one you are searching for.
      </p>
      <Callout type="warning">
        <strong>&ldquo;No wanted criminals loaded&rdquo;?</strong> Open the Fortune Hunters page in the
        game&rsquo;s Journal once &mdash; the game only fetches the list from the server there.
      </Callout>
      <p className="text-sm text-gray-400 mb-4">
        On the right, the <strong className="text-white">SEARCH</strong> card:
      </p>
      <DocTable
        headers={['Control', 'What it does']}
        rows={[
          [<strong className="text-white">Wanted person</strong>, 'The one person you want. The list has a search box at the top: type any part of a name (or a rarity) to filter it. Each entry shows the name and the rarities that person comes in.'],
          [<strong className="text-white">Slot</strong>, '“Any slot”, or “Slot 1” to “Slot 4”. Slots you have accepted are marked “(accepted)”.'],
          [<strong className="text-white">Refresh all fee</strong>, 'What one refresh costs in Andermant.'],
          [<strong className="text-white">Andermant</strong>, 'Your current balance.'],
          [<strong className="text-white">START SEARCHING / STOP</strong>, 'Starts the search, or stops it. The choices are locked while it runs.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        The game has a single refresh button, and it refreshes every slot you have not accepted. The
        bot presses that button, one refresh at a time, until your person is in your slot. If they
        turn up in a different slot, it keeps going &mdash; pick{' '}
        <strong className="text-white">Any slot</strong> if any slot will do. With Any slot, a person
        sitting in a slot you have already accepted does not count.
      </p>
      <Callout type="warning">
        <strong>An accepted slot is never refreshed by the game</strong>, so it cannot be the target.
        Instead of starting, the card tells you in orange: &ldquo;Slot 2 is accepted - the game does
        not refresh it.&rdquo;, with Any slot &ldquo;Every slot is accepted - nothing to
        refresh.&rdquo;, or &ldquo;Slot 4 is not in the list.&rdquo; when the list has fewer slots
        than that.
      </Callout>

      <Shot
        name="quests-02-fortune-hunters.png"
        tab="QUESTS › Fortune Hunters"
        caption="The wanted list on the left; on the right the search for Cyris Wallach in slot 3, found after 23 refreshes."
        shoot="menushot (stage quests): a QUESTS › Fortune Hunters lap — FORTUNE HUNTERS a 4 slottal (Slot 1 accepted, Slot 3 Cyris Wallach Epic), jobbra SEARCH (Wanted person Cyris Wallach - Epic, Slot 3, Refresh all fee 25, Andermant 2450, START SEARCHING, Status found (23 refreshes), Andermant spent 575)."
      />

      <DocHeading>Why Didn&rsquo;t It&hellip;</DocHeading>
      <DocTable
        headers={['Problem', 'Answer']}
        rows={[
          [<strong className="text-white">The button says CHOOSE A REWARD ABOVE</strong>, 'No row has a reward picked. Choose a Reward (and an Amount) in one NPC’s row.'],
          [<strong className="text-white">REROLL is greyed out even with a reward picked</strong>, 'That NPC has no challenge on offer right now — you already accepted or completed it.'],
          [<strong className="text-white">The card says it is waiting for a character</strong>, 'The challenges are read once a character is loaded. Log in and enter the world.'],
          [<strong className="text-white">The list says “The wanted list is not readable yet.”</strong>, 'The bot cannot read the game’s wanted list yet — usually because no character is loaded. Log in and enter the world.'],
          [<strong className="text-white">START SEARCHING is greyed out</strong>, 'No Wanted person is picked yet, the wanted list is not loaded (open the Fortune Hunters page in the game’s Journal once), or an orange line under Slot tells you the chosen slot cannot be refreshed.'],
          [<strong className="text-white">It found the person but kept going</strong>, 'They came up in a different slot from the one you picked. Choose Any slot if the slot does not matter.'],
          [<strong className="text-white">It stopped with “not enough currency”</strong>, 'You ran out of Andermant for the next fee. Nothing more was spent.'],
          [<strong className="text-white">I can’t change the choices</strong>, 'They are locked while it runs. Press STOP first.'],
        ]}
      />
    </div>
  );
}

export default PageQuests;
